/**
 * Report templates, issuance, delivery and public QR verification — module
 * C9, Phase 10.
 *
 * ===========================================================================
 * READ CLAUDE.md SECTIONS 4, 6.1 AND 8 BEFORE ADDING ANYTHING HERE.
 * ===========================================================================
 *
 * A REPORT IS A CONTROLLED DOCUMENT. It presents results that were already
 * performed, reviewed and approved (Phases 8 and 9); it does not compute,
 * interpret or judge one of them. Five ideas govern every shape below, each an
 * acceptance criterion rather than a preference:
 *
 *  1. NOTHING HERE IS TEST SCIENCE. There is no input field, reading, unit,
 *     formula, rounding rule, acceptance limit, chart, axis or specialised
 *     layout in this file, and there must never be one — not for the five
 *     initial tests and not for any test. The only result content a report
 *     carries is `GeneralResultContent`, reused from `execution.ts` exactly as
 *     the performer wrote it and the approver approved it. One layout exists,
 *     `general` (D112); a specialised one arrives with Phase 13, behind the
 *     validated-worked-example gate.
 *
 *  2. ISSUANCE IS NOT DELIVERY. They are separate acts, separate records and
 *     separate statuses (`CLAUDE.md` section 8, D121). Issuing produces a
 *     document; it hands it to nobody. A staff member downloading the file is
 *     not a client receiving it. The system SENDS NOTHING in Phase 10, and
 *     every delivery view says so in `sentBySystem: false` — that is the
 *     actual send result, and claiming any other would be a false confirmation.
 *
 *  3. AN ISSUED VERSION IS IMMUTABLE. Its bytes are rendered once and stored
 *     once, with their hash and size, and nothing regenerates them (D116).
 *     What it was rendered FROM is frozen beside it as `ReportSnapshot`, so a
 *     later change to a logo, a client's name or a template can never alter
 *     what a client was handed. Corrections produce a NEW version; the old one
 *     is retained as `superseded`. Cancellation keeps the number and the file;
 *     numbers are never reused.
 *
 *  4. THE QR CONFIRMS SOURCE AND VERSION STATUS. It confirms nothing about the
 *     material (`CLAUDE.md` section 8). The public surface is an exact
 *     allow-list, `PUBLIC_VERIFICATION_FIELDS`, and a test compares the real
 *     response against it key for key.
 *
 *  5. NO LABORATORY'S OWN VALUES LIVE HERE (`CLAUDE.md` section 6.1). No name,
 *     address, footer wording, numbering prefix or template. The footer text
 *     has NO product default: `null` means null, because no wording this
 *     product could supply belongs to any laboratory (D126).
 */
import { LOCALES } from './common.js';
// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------
export const REPORT_TEMPLATE_STATUS = {
    ACTIVE: 'active',
    /**
     * Withdrawn from use for NEW reports. Terminal, and it never touches a
     * report already issued from it: an issued version carries the template's
     * revision frozen in its snapshot, so retiring a template changes no
     * document that exists.
     */
    RETIRED: 'retired',
};
export const ALL_REPORT_TEMPLATE_STATUSES = Object.freeze(Object.values(REPORT_TEMPLATE_STATUS));
/**
 * The layouts a template may use.
 *
 * ONE exists (D112), and it renders the general result recorded in Phase 8 and
 * nothing else. Every table, chart or calculation that differs per test is
 * test science, gated on a laboratory-validated worked example
 * (`CLAUDE.md` section 4). A laboratory may hold several templates on this one
 * layout — its own names and language sets — which is configuration, not a
 * second layout.
 */
export const REPORT_TEMPLATE_LAYOUT = {
    GENERAL: 'general',
};
export const ALL_REPORT_TEMPLATE_LAYOUTS = Object.freeze(Object.values(REPORT_TEMPLATE_LAYOUT));
/**
 * The language a report is rendered in.
 *
 * The same two values as `LOCALES`, reused rather than restated so the
 * interface and the document can never disagree about what "ar" means. It is
 * its own name because it is a different decision from a user's preferred
 * locale. A version is ISSUED in one language, chosen when it is prepared — the
 * language of the rendering stored at issuance, which the QR page reports.
 * Since D330 the same issued version can ALSO be downloaded in the other
 * language, rendered from the same frozen snapshot: one version, one number,
 * two presentations. Which one is produced is always chosen explicitly, never
 * taken from the interface language.
 */
export const REPORT_LANGUAGE = {
    AR: LOCALES.AR,
    EN: LOCALES.EN,
};
export const ALL_REPORT_LANGUAGES = Object.freeze(Object.values(REPORT_LANGUAGE));
/** A report language is a locale; this is the compile-time proof of it. */
const _reportLanguageIsALocale = true;
void _reportLanguageIsALocale;
// ---------------------------------------------------------------------------
// Delivery — the act that is NOT issuance
// ---------------------------------------------------------------------------
/**
 * Why a document left the laboratory.
 *
 * `delivery` is the client receiving the report. `notice` is telling somebody
 * who already received one that its status changed — recorded the same way,
 * and kept apart, because a notice about a cancelled report is not a delivery
 * of a report, and a register that confused the two would report work as
 * delivered that never was.
 */
export const REPORT_DELIVERY_PURPOSE = {
    DELIVERY: 'delivery',
    NOTICE: 'notice',
};
export const ALL_REPORT_DELIVERY_PURPOSES = Object.freeze(Object.values(REPORT_DELIVERY_PURPOSE));
/**
 * How it was handed over, as the person recording it states.
 *
 * `email_outside_system` is named exactly that because the system has no email
 * transport: somebody sent it from their own mailbox and is recording what
 * they did. Nothing in this list is something the product performs (D121).
 */
export const REPORT_DELIVERY_METHOD = {
    IN_PERSON: 'in_person',
    COURIER: 'courier',
    POSTAL: 'postal',
    EMAIL_OUTSIDE_SYSTEM: 'email_outside_system',
    OTHER: 'other',
};
export const ALL_REPORT_DELIVERY_METHODS = Object.freeze(Object.values(REPORT_DELIVERY_METHOD));
// ---------------------------------------------------------------------------
// Corrections to an ISSUED report
// ---------------------------------------------------------------------------
/**
 * What a correction to an issued report changes.
 *
 * `administrative` is the document being wrong about itself — a misspelt
 * client name, the wrong project — and it re-renders from the corrected linked
 * records without reopening any result.
 *
 * `scientific` is the RESULT having changed, and the report does not change
 * it: the result's own Phase 9 correction does (D102), and the replacement
 * version waits until each affected result has been approved again after the
 * correction was opened (D118). The two procedures therefore cannot disagree
 * about what was approved.
 */
export const REPORT_CORRECTION_IMPACT = {
    ADMINISTRATIVE: 'administrative',
    SCIENTIFIC: 'scientific',
};
export const ALL_REPORT_CORRECTION_IMPACTS = Object.freeze(Object.values(REPORT_CORRECTION_IMPACT));
/**
 * A scientific correction must name the results it concerns; an administrative
 * one has none to name. One place, so the schema and the service cannot drift.
 */
export function correctionRequiresAffectedResults(impact) {
    return impact === REPORT_CORRECTION_IMPACT.SCIENTIFIC;
}
// ---------------------------------------------------------------------------
// Payment hold — access, never validity
// ---------------------------------------------------------------------------
/**
 * Whether client-facing ACCESS to the issued file is held for payment (D115).
 *
 * READ THIS BEFORE USING IT ANYWHERE: a hold changes the version status not at
 * all, the report status not at all, the verification outcome not at all, and
 * the validity of no result. It refuses a delivery and a public download, and
 * that is the whole of it. Staff access is unaffected: a laboratory answering
 * a client's question needs the document it issued.
 *
 * It is decided AT FIRST ISSUANCE from the laboratory's setting, so switching
 * the setting on later never retroactively holds a report already issued.
 */
export const REPORT_PAYMENT_HOLD = {
    NONE: 'none',
    HELD: 'held',
    /** Released by a named person who records payments, with a note — or, from
     *  Phase 11, automatically when the work order is settled (D145). Never
     *  held again. */
    RELEASED: 'released',
};
export const ALL_REPORT_PAYMENT_HOLDS = Object.freeze(Object.values(REPORT_PAYMENT_HOLD));
// ---------------------------------------------------------------------------
// What a Phase 9 result correction means for a report already issued (D117)
// ---------------------------------------------------------------------------
/**
 * The answer to the question Phase 9 deferred: a result included in an issued
 * report has been corrected — now what?
 *
 * NOTHING AUTOMATIC. The system never suspends, re-issues or invalidates a
 * document by itself, because whether a changed result invalidates a report is
 * a judgement the laboratory makes, and a status nobody decided is a status
 * nobody can defend. What the system must never do is let the question go
 * unseen, so this state is SHOWN on the report.
 *
 * It is COMPUTED AT READ TIME from the revision the report recorded and the
 * result's current approval — never stored — so no flag can drift out of
 * agreement with the results it describes.
 *
 * The honest consequence, recorded as a limitation: between a correction and a
 * person's decision, the public page still reads "issued and valid".
 */
export const RESULT_CORRECTION_IMPACT_STATE = {
    /** The reported revision is still the approved one. */
    NONE: 'none',
    /** A Phase 9 correction is open on this result right now. */
    CORRECTION_OPEN: 'correction_open',
    /**
     * The re-approved revision differs from the one this report printed — or no
     * active lock exists at all. A person decides what to do; the comparison
     * errs towards this answer rather than towards silence.
     */
    REPLACEMENT_REQUIRED: 'replacement_required',
    /** The correction concluded and the approved revision matches what was
     *  printed. The document still says what the record says. */
    CONCLUDED_WITHOUT_CHANGE: 'concluded_without_change',
};
export const ALL_RESULT_CORRECTION_IMPACT_STATES = Object.freeze(Object.values(RESULT_CORRECTION_IMPACT_STATE));
/**
 * Which of those states a register should draw a person's attention to.
 *
 * It marks a row for a human to look at. It changes no status by itself, and
 * nothing downstream may treat it as though it had.
 */
export function resultCorrectionNeedsAttention(state) {
    return (state === RESULT_CORRECTION_IMPACT_STATE.CORRECTION_OPEN ||
        state === RESULT_CORRECTION_IMPACT_STATE.REPLACEMENT_REQUIRED);
}
// ---------------------------------------------------------------------------
// THE PUBLIC SURFACE — the only unauthenticated view of a laboratory's records
// ---------------------------------------------------------------------------
/**
 * THE COMPLETE LIST OF WHAT AN UNAUTHENTICATED VISITOR IS TOLD.
 *
 * ===========================================================================
 * ADDING A KEY HERE PUBLISHES IT TO ANYONE HOLDING A PRINTED LINK. Read this
 * whole comment before you do.
 * ===========================================================================
 *
 * A QR code is printed on a document that leaves the laboratory, so whoever
 * holds the paper holds the link — a client, a contractor, a consultant, a
 * ministry, or somebody who found it in a skip. The page exists to answer ONE
 * question: is this document what it claims to be, and is that claim still
 * current? Anything beyond that answer is another party's confidential
 * business.
 *
 * NEVER on this surface, in a body or a header: the client, the project, the
 * location, the sample, the work order, any result or remark, any person, any
 * file id or storage key, the tenant's id or slug, and the payment hold or its
 * reason. `testTitles` is test NAMES only — what was tested, never what was
 * found.
 *
 * And what the page confirms is the SOURCE AND VERSION STATUS of a document.
 * It is not a statement that the material passed anything (`CLAUDE.md`
 * section 8): QR validity is not scientific correctness, and the page says so
 * in both languages.
 *
 * A test asserts that the real HTTP response's key set is exactly this list.
 */
export const PUBLIC_VERIFICATION_FIELDS = Object.freeze([
    'status',
    'laboratoryName',
    'reportNumber',
    'versionNumber',
    'testTitles',
    'issuedAt',
    'pageCount',
    'language',
    'latestVersion',
    'originalAvailable',
]);
/**
 * The shape of a public verification token as it appears in a URL.
 *
 * 32 random bytes from `crypto.randomBytes`, base64url-encoded: 43 characters,
 * no padding (D122). It is a PUBLIC REFERENCE that must be unguessable, not a
 * secret that must be irrecoverable — it is printed on paper the laboratory
 * hands over, and staff must be able to open the very link they printed.
 *
 * Matching the shape before any database lookup is what lets a malformed token
 * be refused with the same `404` as an unknown one, without a query: it blunts
 * enumeration, keeps abuse off the database, and tells a guesser nothing about
 * which of their guesses was better formed.
 */
export const REPORT_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;
/** Length of the base64url encoding of 32 bytes, unpadded. */
export const REPORT_TOKEN_LENGTH = 43;
/**
 * Whether a string could be a verification token at all.
 *
 * It says NOTHING about whether such a token exists, which report it names, or
 * whether that report is valid — each of those needs a lookup, and the two
 * answers must never be confused.
 */
export function isReportVerificationToken(value) {
    return REPORT_TOKEN_PATTERN.test(value);
}
//# sourceMappingURL=reports.js.map